import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite, useUrl } from "@/context/SiteContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Eye, Plus, Edit, LogOut, RefreshCw } from "lucide-react";
import ContentEditor from "@/pages/admin/ContentEditor";
import ThemeEditor from "@/pages/admin/ThemeEditor";
import MediaManager from "@/pages/admin/MediaManager";
import DepartmentsEditor from "@/pages/admin/DepartmentsEditor";
import SitemapPanel from "@/pages/admin/SitemapPanel";
import RichTextEditor from "@/components/RichTextEditor";
import { useDepartments } from "@/context/DepartmentsContext";

function slugify(t) {
  return t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function AdminDashboardPage() {
  const { user, checking, logout } = useAuth();
  const { refresh: refreshSite } = useSite();
  const { refresh: refreshDepartments } = useDepartments();
  const [tab, setTab] = useState("contacts");

  if (checking) return <div className="p-12 font-mono-label" data-testid="admin-checking">Se verifică sesiunea...</div>;
  if (!user) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-[calc(100vh-80px)]" data-testid="admin-dashboard">
      <div className="border-b border-black">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-10 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono-label text-black/60">Admin</div>
            <h1 className="mt-2 font-display font-medium uppercase text-3xl md:text-5xl tracking-tighter">
              Panou Administrare
            </h1>
            <div className="mt-2 text-sm text-black/60 font-mono-label">Autentificat: {user.email}</div>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="font-mono-label border border-black px-5 py-3 hover:bg-black hover:text-white inline-flex items-center gap-2">
            <LogOut size={14} /> Deconectare
          </button>
        </div>
      </div>

      <div className="border-b border-black bg-white sticky top-0 z-30">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 flex overflow-x-auto">
          {[
            { id: "contacts", label: "Mesaje" },
            { id: "articles", label: "Articole" },
            { id: "departments", label: "Departamente" },
            { id: "content", label: "Conținut pagini" },
            { id: "theme", label: "Temă (culori & fonturi)" },
            { id: "media", label: "Media" },
            { id: "sitemap", label: "URL-uri / Sitemap" },
          ].map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`font-mono-label px-6 py-4 border-r border-black whitespace-nowrap ${tab === t.id ? "bg-black text-white" : "hover:bg-black/5"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-10">
        {tab === "contacts" && <ContactsPanel />}
        {tab === "articles" && <ArticlesPanel />}
        {tab === "departments" && <DepartmentsEditor onSaved={refreshDepartments} />}
        {tab === "content" && <ContentEditor onSaved={refreshSite} />}
        {tab === "theme" && <ThemeEditor onSaved={refreshSite} />}
        {tab === "media" && <MediaManager />}
        {tab === "sitemap" && <SitemapPanel />}
      </div>
    </div>
  );
}

function ContactsPanel() {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setItems(null);
    try {
      const { data } = await api.get("/admin/contacts");
      setItems(data);
    } catch (e) { setItems([]); toast.error("Eroare la încărcare"); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Ștergi acest mesaj?")) return;
    await api.delete(`/admin/contacts/${id}`);
    toast.success("Mesaj șters");
    setSelected(null);
    load();
  };
  const toggleRead = async (item) => {
    await api.patch(`/admin/contacts/${item.id}?read=${!item.read}`);
    load();
  };

  if (items === null) return <div className="font-mono-label">Se încarcă...</div>;

  return (
    <div data-testid="contacts-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono-label text-black/60">Mesaje primite</div>
          <div className="font-display font-medium uppercase text-2xl tracking-tight mt-1">{items.length} mesaje</div>
        </div>
        <button onClick={load} data-testid="refresh-contacts" className="font-mono-label border border-black px-4 py-2 hover:bg-black hover:text-white inline-flex items-center gap-2">
          <RefreshCw size={14} /> Reîncarcă
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-black p-12 text-center">
          <div className="font-mono-label text-black/60">Nu există mesaje încă.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-6 border border-black divide-y divide-black">
            {items.map((c) => (
              <button key={c.id} onClick={() => setSelected(c)} data-testid={`contact-item-${c.id}`}
                className={`w-full text-left p-5 hover:bg-black hover:text-white transition-all ${selected?.id === c.id ? "bg-black text-white" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold uppercase">{c.name}</div>
                  {!c.read && <span className="font-mono-label text-[10px] bg-white text-black border border-black px-2 py-0.5">NOU</span>}
                </div>
                <div className="text-sm opacity-70 mt-1">{c.email} {c.department ? `· ${c.department}` : ""}</div>
                <div className="text-xs font-mono-label opacity-50 mt-2">{new Date(c.created_at).toLocaleString("ro-RO")}</div>
              </button>
            ))}
          </div>
          <div className="lg:col-span-6">
            {selected ? (
              <div className="border border-black p-8" data-testid="contact-detail">
                <div className="font-mono-label text-black/60">Detalii</div>
                <div className="mt-2 font-display font-medium uppercase text-3xl">{selected.name}</div>
                <div className="mt-4 space-y-2 text-sm">
                  <div><span className="font-mono-label text-black/60">Email:</span> {selected.email}</div>
                  {selected.phone && <div><span className="font-mono-label text-black/60">Telefon:</span> {selected.phone}</div>}
                  {selected.department && <div><span className="font-mono-label text-black/60">Departament:</span> {selected.department}</div>}
                  <div><span className="font-mono-label text-black/60">Data:</span> {new Date(selected.created_at).toLocaleString("ro-RO")}</div>
                </div>
                <div className="mt-6">
                  <div className="font-mono-label text-black/60 mb-2">Mesaj</div>
                  <div className="whitespace-pre-wrap text-black/85 border-t border-black pt-4">{selected.message}</div>
                </div>
                <div className="mt-8 flex gap-3">
                  <button onClick={() => toggleRead(selected)} data-testid="mark-read-btn" className="font-mono-label border border-black px-4 py-2 hover:bg-black hover:text-white inline-flex items-center gap-2">
                    <Eye size={14} /> {selected.read ? "Marchează necitit" : "Marchează citit"}
                  </button>
                  <button onClick={() => del(selected.id)} data-testid="delete-contact-btn" className="font-mono-label border border-black px-4 py-2 hover:bg-red-600 hover:text-white hover:border-red-600 inline-flex items-center gap-2">
                    <Trash2 size={14} /> Șterge
                  </button>
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-black/40 p-12 text-center font-mono-label text-black/60">
                Selectează un mesaj
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ArticlesPanel() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const url = useUrl();

  const empty = {
    title: "", slug: "", excerpt: "", content: "", cover_image: "", published: true,
    meta_title: "", meta_description: "", og_image: "",
  };

  const load = async () => {
    setItems(null);
    try {
      const { data } = await api.get("/admin/articles");
      setItems(data);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (!editing.title || !editing.slug) return toast.error("Titlu și slug sunt obligatorii");
      if (editing.id) {
        await api.put(`/admin/articles/${editing.id}`, editing);
        toast.success("Articol actualizat");
      } else {
        await api.post("/admin/articles", editing);
        toast.success("Articol creat");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const del = async (id) => {
    if (!window.confirm("Ștergi acest articol?")) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Șters");
    load();
  };

  if (editing) {
    const input = "w-full bg-white border border-black rounded-none px-4 py-3 focus:outline-none";
    return (
      <div data-testid="article-editor">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="font-mono-label text-black/60">{editing.id ? "Editare articol" : "Articol nou"}</div>
            <div className="font-display font-medium uppercase text-2xl mt-1">{editing.title || "Fără titlu"}</div>
          </div>
          <button onClick={() => setEditing(null)} data-testid="cancel-edit-btn" className="font-mono-label border border-black px-4 py-2">Anulează</button>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="font-mono-label text-black/60 block mb-2">Titlu *</label>
            <input data-testid="article-title-input" className={input} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.slug || slugify(e.target.value) })} />
          </div>
          <div>
            <label className="font-mono-label text-black/60 block mb-2">Slug (URL) *</label>
            <input data-testid="article-slug-input" className={input} value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} />
          </div>
          <div>
            <label className="font-mono-label text-black/60 block mb-2">Extras</label>
            <textarea data-testid="article-excerpt-input" rows={2} className={input} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
          </div>
          <div>
            <label className="font-mono-label text-black/60 block mb-2">Imagine cover (URL)</label>
            <input data-testid="article-image-input" className={input} value={editing.cover_image || ""} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} placeholder="https://..." />
            {editing.cover_image && (
              <div className="mt-3 border border-black inline-block p-2 bg-white" data-testid="cover-preview">
                <img src={editing.cover_image} alt="preview" className="max-h-40 w-auto" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            )}
          </div>
          <div>
            <label className="font-mono-label text-black/60 block mb-2">Conținut *</label>
            <RichTextEditor
              testId="article-content-editor"
              value={editing.content || ""}
              onChange={(html) => setEditing({ ...editing, content: html })}
            />
          </div>

          {/* SEO */}
          <div className="border border-black p-5 space-y-4 bg-black/5" data-testid="article-seo-block">
            <div className="font-mono-label text-black/60">Optimizare SEO (opțional)</div>
            <div>
              <label className="font-mono-label text-black/50 text-[10px] block mb-1">Meta title (max 60 caractere recomandat)</label>
              <input
                data-testid="article-meta-title"
                className={input}
                value={editing.meta_title || ""}
                onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })}
                placeholder={editing.title || "Titlul afișat în Google (dacă e gol, se folosește titlul articolului)"}
              />
              <div className="font-mono-label text-black/40 text-[10px] mt-1">{(editing.meta_title || "").length} / 60</div>
            </div>
            <div>
              <label className="font-mono-label text-black/50 text-[10px] block mb-1">Meta description (max 160 caractere recomandat)</label>
              <textarea
                data-testid="article-meta-description"
                rows={2}
                className={input}
                value={editing.meta_description || ""}
                onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })}
                placeholder={editing.excerpt || "Descrierea afișată în Google (dacă e gol, se folosește extrasul)"}
              />
              <div className="font-mono-label text-black/40 text-[10px] mt-1">{(editing.meta_description || "").length} / 160</div>
            </div>
            <div>
              <label className="font-mono-label text-black/50 text-[10px] block mb-1">og:image (URL — imagine pentru share pe LinkedIn/Facebook/Twitter)</label>
              <input
                data-testid="article-og-image"
                className={input}
                value={editing.og_image || ""}
                onChange={(e) => setEditing({ ...editing, og_image: e.target.value })}
                placeholder={editing.cover_image || "https://... (dacă e gol, se folosește imaginea cover)"}
              />
              {editing.og_image && (
                <div className="mt-3 border border-black inline-block p-2 bg-white" data-testid="og-preview">
                  <img src={editing.og_image} alt="og preview" className="max-h-32 w-auto" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              )}
            </div>
          </div>
          <label className="flex items-center gap-3 font-mono-label">
            <input type="checkbox" data-testid="article-published-input" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />
            Publicat
          </label>
          <button onClick={save} data-testid="save-article-btn" className="font-mono-label bg-black text-white px-6 py-4 border border-black hover:bg-white hover:text-black w-fit">
            {editing.id ? "Salvează modificările" : "Publică articolul"}
          </button>
        </div>
      </div>
    );
  }

  if (items === null) return <div className="font-mono-label">Se încarcă...</div>;

  return (
    <div data-testid="articles-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono-label text-black/60">Articole blog</div>
          <div className="font-display font-medium uppercase text-2xl tracking-tight mt-1">{items.length} articole</div>
        </div>
        <button onClick={() => setEditing(empty)} data-testid="new-article-btn" className="font-mono-label bg-black text-white px-4 py-2 hover:bg-white hover:text-black border border-black inline-flex items-center gap-2">
          <Plus size={14} /> Articol nou
        </button>
      </div>
      {items.length === 0 ? (
        <div className="border border-black p-12 text-center">
          <div className="font-mono-label text-black/60">Nu există articole încă.</div>
        </div>
      ) : (
        <div className="border border-black divide-y divide-black">
          {items.map((a) => (
            <div key={a.id} data-testid={`admin-article-${a.slug}`} className="p-5 flex items-center justify-between gap-4">
              <div className="flex-1">
                <div className="font-display font-bold uppercase">{a.title}</div>
                <div className="text-sm text-black/60 font-mono-label mt-1">/{a.slug} · {a.published ? "PUBLICAT" : "DRAFT"} · {new Date(a.created_at).toLocaleDateString("ro-RO")}</div>
              </div>
              <div className="flex gap-2">
                <Link to={url.article(a.slug)} target="_blank" className="font-mono-label border border-black px-3 py-2 hover:bg-black hover:text-white"><Eye size={14} /></Link>
                <button onClick={() => setEditing(a)} data-testid={`edit-article-${a.slug}`} className="font-mono-label border border-black px-3 py-2 hover:bg-black hover:text-white"><Edit size={14} /></button>
                <button onClick={() => del(a.id)} data-testid={`delete-article-${a.slug}`} className="font-mono-label border border-black px-3 py-2 hover:bg-red-600 hover:text-white hover:border-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
